/*
 * One email in its own sandboxed document, for both readers: the reply agent's thread (`main.tsx`) and
 * the master inbox's (`inbox-v2/thread.tsx`). The frame is sized to what it renders; quoted history the
 * earlier messages already hold folds behind the incumbent "Show quoted history" pill; pictures come
 * through the server's proxy and are shown by default (rendering plan, slice 3), unless this person
 * hides them in the workspace, when one email can still show them without changing that choice.
 *
 * The frame runs no script and fetches nothing: shown pictures are `blob:` addresses the page made from
 * the proxy's answers (`mail-images.ts`). Folded history, hidden pictures and a message that is not on
 * screen ask for nothing.
 *
 * In the dark theme, mail its author styled is made dark (`email-dark.ts`) and offers "Show original",
 * the author's own light page for this one email until the rep goes back; nothing remembers the choice.
 */
import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { darkAdaptedEmail } from "../../src/email-dark.ts";
import type { EmailDisplay } from "../../src/email-display.ts";
import { EMAIL_FRAME_SANDBOX, type EmailFrameLook } from "../../src/email-frame.ts";
import { emailStyling, withoutNeutralColours } from "../../src/email-styling.ts";
import { SHOW_MESSAGE_EVENT } from "../agent/conversation-shared.ts";
import { MoonIcon, MoreIcon, SunIcon } from "../icons.tsx";
import { DarkThemeContext } from "../shell/theme.ts";
import { useSettledAt } from "./frame-settle.ts";
import {
  type AssetState,
  frameBody,
  hasPictures,
  type ImageAsset,
  pairImages,
  pictureShown,
  planTags,
  readerFrameDocument,
  wantedAssets,
} from "./mail-images.ts";
import {
  messageImagesPath,
  useAssets,
  useImageManifest,
  useImagePreference,
  useNear,
} from "./use-message-images.ts";
import "./mail-frame.css";

/*
 * Mail designed at a fixed width, such as a 600 px newsletter, is scaled down to the frame rather than
 * cut off, as mail clients do on a phone. Returns the height the frame needs. The frame runs no script,
 * so this page does the measuring; it can, because the frame shares its origin.
 */
function fitEmail(element: HTMLIFrameElement | null): number | null {
  const document = element?.contentDocument;
  const body = document?.body;
  if (!document || !body) return null;
  body.style.removeProperty("width");
  body.style.removeProperty("transform");
  const available = document.documentElement.clientWidth;
  const needed = body.scrollWidth;
  if (needed > available + 1) {
    body.style.width = `${needed}px`;
    body.style.transformOrigin = "0 0";
    body.style.transform = `scale(${available / needed})`;
  }
  return Math.ceil(body.getBoundingClientRect().height);
}

/** An unreadable frame keeps its last height rather than collapsing. */
function measureFrame(element: HTMLIFrameElement | null, set: (height: number) => void): void {
  const height = fitEmail(element);
  if (height !== null) set(height);
}

/** Puts each settled picture into the written document in place, so the frame never reloads for it. */
function applyStates(element: HTMLIFrameElement | null, states: ReadonlyMap<string, AssetState>) {
  const document = element?.contentDocument;
  if (!document) return;
  for (const image of document.querySelectorAll<HTMLImageElement>("img[data-lcr-asset]")) {
    const state = states.get(image.getAttribute("data-lcr-asset") ?? "");
    if (!state || state.status === "loading") continue;
    image.removeAttribute("data-lcr-loading");
    if (state.status === "shown") {
      // A picture with no declared size starts as the frame's held stand-in (`withImagePlaceholders`),
      // whose rule caps it at 160 by 40 px. The marker goes with the stand-in, or the real picture
      // stays a thumbnail.
      image.removeAttribute("data-placeholder");
      if (image.getAttribute("src") !== state.url) image.setAttribute("src", state.url);
    } else if (state.status === "blocked") image.setAttribute("data-lcr-off", "");
    else image.replaceWith(unavailableNote(document, image, state.status === "unsupported"));
  }
}

/** Unavailable, unsupported or failed: the picture's words in its place, as an embedded one says them. */
function unavailableNote(
  document: Document,
  image: HTMLImageElement,
  unsupported: boolean,
): HTMLSpanElement {
  const note = document.createElement("span");
  note.className = "lcr-image-note";
  note.setAttribute("role", "img");
  const alt = image.getAttribute("alt")?.trim();
  const what = unsupported ? "Image format not shown here" : "Image unavailable";
  note.textContent = alt ? `${what}: ${alt}` : what;
  note.setAttribute("aria-label", note.textContent);
  return note;
}

/** What the rep is told about this email's pictures, in one sentence; null when all is well. */
type PictureNote =
  | { kind: "hidden"; count: number }
  | { kind: "missing"; count: number; retry: boolean; unsupported: boolean }
  | { kind: "unsupported" };

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

function noteOf(input: {
  hiddenByChoice: number;
  states: ReadonlyMap<string, AssetState>;
  wanted: readonly ImageAsset[];
  unsupported: boolean;
}): PictureNote | null {
  if (input.unsupported) return { kind: "unsupported" };
  const settled = input.wanted.map((asset) => input.states.get(asset.id)?.status);
  const blocked = settled.filter((status) => status === "blocked").length;
  if (input.hiddenByChoice + blocked > 0)
    return { kind: "hidden", count: input.hiddenByChoice + blocked };
  const lost = settled.filter((status) => status === "failed" || status === "busy").length;
  const gone = settled.filter((status) => status === "unavailable").length;
  const unshown = settled.filter((status) => status === "unsupported").length;
  const count = lost + gone + unshown;
  if (count > 0) return { kind: "missing", count, retry: lost > 0, unsupported: unshown === count };
  return null;
}

function PictureNoteView(props: { note: PictureNote; onShow: () => void; onRetry: () => void }) {
  const { note } = props;
  if (note.kind === "unsupported")
    return (
      <p className="lcr-mail-note lcr-picture-note">Images in this email can't be shown here.</p>
    );
  if (note.kind === "hidden")
    return (
      <p className="lcr-mail-note lcr-picture-note">
        <span>
          {note.count} {plural(note.count, "image", "images")} hidden: you hide images from senders
          in this workspace.
        </span>
        <button type="button" className="lcr-small-button" onClick={props.onShow}>
          Show in this email
        </button>
      </p>
    );
  return (
    <p className="lcr-mail-note lcr-picture-note">
      <span>
        {note.count} {plural(note.count, "image", "images")}{" "}
        {note.unsupported
          ? `${plural(note.count, "uses", "use")} a format the reader does not show.`
          : "could not be shown."}
      </span>
      {note.retry && (
        <button type="button" className="lcr-linklike" onClick={props.onRetry}>
          Try again
        </button>
      )}
    </p>
  );
}

/** Image attachments, previewed through the same proxy; each opens its normal download. */
function Previews(props: {
  assets: readonly ImageAsset[];
  states: ReadonlyMap<string, AssetState>;
  attachments: readonly { id: string; name: string }[];
  attachmentHref: (id: string) => string;
}) {
  const shown = props.assets.flatMap((asset) => {
    const file = props.attachments.find((candidate) => candidate.id === asset.attachmentId);
    const state = props.states.get(asset.id);
    if (!file || !state || ["unavailable", "unsupported", "failed"].includes(state.status))
      return [];
    return [{ asset, file, state }];
  });
  if (!shown.length) return null;
  return (
    <ul className="lcr-mail-previews" aria-label="Image attachments">
      {shown.map(({ asset, file, state }) => (
        <li key={asset.id}>
          <a href={props.attachmentHref(file.id)} download={file.name}>
            {state.status === "shown" ? (
              <img src={state.url} alt="" />
            ) : (
              <span className="lcr-mail-preview-wait" aria-hidden="true" />
            )}
            <span className="lcr-mail-preview-name">{file.name}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

export type MailFrameImages = {
  readonly workspaceId: string;
  readonly planId: string;
  readonly messageId: string;
};

/** Image attachments beside the inline pictures; one a mapped CID already shows is not shown again. */
function previewAssets(assets: readonly ImageAsset[] | null): ImageAsset[] {
  const mapped = new Set(
    (assets ?? []).flatMap((asset) =>
      asset.kind === "cid_resolved" && asset.attachmentId ? [asset.attachmentId] : [],
    ),
  );
  return (assets ?? []).filter(
    (asset) => asset.kind === "attachment" && !mapped.has(asset.attachmentId ?? ""),
  );
}

/** Plain mail follows the theme; styled mail is made dark in the dark theme unless asked for its own. */
function lookOf(plain: boolean, dark: boolean, original: boolean): EmailFrameLook {
  if (plain) return dark ? "app-dark" : "app-light";
  return dark && !original ? "email-dark" : "email";
}

/** The body as the look frames it: the author's own, made dark, or without its neutral colours. */
function framedBody(body: string, look: EmailFrameLook): string {
  if (look === "email") return body;
  return look === "email-dark" ? darkAdaptedEmail(body).html : withoutNeutralColours(body);
}

/**
 * Everything an email's pictures need, asked in order: the preference, then (once the frame is near
 * the screen) the manifest, then the pictures the planned document will show. The look is decided
 * here too, on what the frame shows: the reply alone while its quoted history is folded, the whole
 * email once the rep shows it, and only the pictures on screen. Showing the history may change it.
 * Styled mail in the dark theme is made dark unless the rep asked for the `original`.
 */
function usePictures(input: {
  display: EmailDisplay;
  images: MailFrameImages | null;
  hasFiles: boolean;
  quoted: boolean;
  near: boolean;
  dark: boolean;
  original: boolean;
}) {
  const { display, images, quoted, dark } = input;
  const [showOnce, setShowOnce] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const pictures = images !== null && (hasPictures(display.html) || input.hasFiles);
  const { preference, settled } = useImagePreference(images?.workspaceId ?? "", pictures);
  const path = images ? messageImagesPath(images.workspaceId, images.planId, images.messageId) : "";
  const manifest = useImageManifest(path, pictures && input.near && settled);
  const blocked = preference.blockExternal && !showOnce;
  const html = quoted ? display.html : display.visibleHtml;
  const pairs = useMemo(
    () => (manifest.assets ? pairImages(display.html, manifest.assets) : null),
    [display.html, manifest.assets],
  );
  const plans = useMemo(() => planTags(html, pairs, blocked), [html, pairs, blocked]);
  const inline = useMemo(() => wantedAssets(plans), [plans]);
  const previews = useMemo(() => previewAssets(manifest.assets), [manifest.assets]);
  const wanted = useMemo(() => [...inline, ...previews], [inline, previews]);
  const states = useAssets(path, wanted, { show: showOnce, attempt });
  // Said only of an email with pictures of its own: one whose files are all documents has none to
  // fail, whatever became of the manifest asked for its image previews.
  const unsupported =
    hasPictures(display.html) && (manifest.failed || (manifest.assets !== null && pairs === null));
  const plain = useMemo(() => {
    const shown = pictureShown({
      html,
      plans,
      states,
      awaiting: pictures && manifest.assets === null && !manifest.failed,
      blocked,
    });
    return emailStyling(html, { pictureShown: shown }) === "plain";
  }, [html, plans, states, pictures, manifest.assets, manifest.failed, blocked]);
  const look = lookOf(plain, dark, input.original);
  // The document is written from the plan alone; pictures arriving later are put in place. Try
  // again writes it afresh, so a picture that failed gets its place back.
  const srcDoc = useMemo(() => {
    const document = readerFrameDocument(framedBody(frameBody(html, plans), look), {
      quoted,
      look,
    });
    return `${document}<!-- ${attempt} -->`;
  }, [html, plans, quoted, look, attempt]);
  return {
    look,
    adaptable: dark && !plain,
    srcDoc,
    states,
    previews,
    loading: wanted.some((asset) => states.get(asset.id)?.status === "loading"),
    note: noteOf({
      hiddenByChoice: [...plans.values()].filter((plan) => plan.kind === "hidden").length,
      states,
      wanted: inline,
      unsupported,
    }),
    showOnce: () => setShowOnce(true),
    retry: () => setAttempt(attempt + 1),
  };
}

/** Keeps the frame as tall as its email, as the pane's width and the pictures change it. */
function useFrameHeight(
  frame: React.RefObject<HTMLIFrameElement | null>,
  srcDoc: string,
  states: ReadonlyMap<string, AssetState>,
): { height: number; measure: () => void } {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    // A narrower pane reflows the email, so its height changes with the frame's width.
    const observer = new ResizeObserver(() => measureFrame(element, setHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [frame]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new `srcDoc` is a new document to fit
  useEffect(() => {
    /*
     * `load` waits for every subresource, and WebKit holds it for a load the policy blocked, so the
     * frame is also measured as each new document or picture settles.
     */
    applyStates(frame.current, states);
    const timers = [60, 300, 1_500].map((ms) =>
      setTimeout(() => measureFrame(frame.current, setHeight), ms),
    );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [frame, srcDoc, states]);
  return { height, measure: () => measureFrame(frame.current, setHeight) };
}

type Toggle = { readonly shown: boolean; readonly toggle: () => void };

/** The messages a folded quote was proved to copy, first region first. */
function quotedIds(display: EmailDisplay): readonly string[] {
  return display.regions.flatMap((region) =>
    region.kind === "duplicate_quote" ? region.representedBy : [],
  );
}

/**
 * What a folded quote holds, where the thread can say it: the message it copies ("your email of
 * 12 Oct") and the way to that message, which the thread already shows. Folding stays the rule's
 * (`src/quoted-history.ts`): only a proved copy of a message in this thread is ever folded. The thread
 * reads newest first and draws every message, so the copied one is always below (LEA-514).
 */
export type QuotedSource = {
  readonly id: string;
  readonly words: string;
};

/** The folded quote, named: what it is, why it is safe to fold (it is shown below), and Show. */
function QuoteFold(props: { frameId: string; quoted: Toggle; source: QuotedSource }) {
  const { quoted, source } = props;
  return (
    <p className="lcr-quote-fold">
      <MoreIcon size={16} />
      <span className="lcr-quote-fold-words">
        Quoted: {source.words},{" "}
        <button
          type="button"
          className="lcr-linklike"
          onClick={() =>
            window.dispatchEvent(new CustomEvent(SHOW_MESSAGE_EVENT, { detail: source.id }))
          }
        >
          shown below
        </button>
      </span>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        className="lcr-linklike lcr-quote-fold-toggle"
        aria-expanded={quoted.shown}
        aria-controls={props.frameId}
        aria-label={`${quoted.shown ? "Hide" : "Show"} the quoted ${source.words}`}
        onClick={quoted.toggle}
      >
        {quoted.shown ? "Hide quote" : "Show quote"}
      </button>
    </p>
  );
}

/** The pills under an email: its quoted history, and its original page where it was made dark. */
function MailActions(props: {
  frameId: string;
  quoted: Toggle | null;
  source: QuotedSource | null;
  original: Toggle | null;
}) {
  const { quoted, original } = props;
  if (!quoted && !original) return null;
  return (
    <div className="lcr-mail-actions">
      {quoted && props.source && (
        <QuoteFold frameId={props.frameId} quoted={quoted} source={props.source} />
      )}
      {quoted && !props.source && (
        <button
          type="button"
          className="lcr-quote-toggle"
          aria-expanded={quoted.shown}
          aria-controls={props.frameId}
          onClick={quoted.toggle}
        >
          <MoreIcon size={16} />
          <span>{`${quoted.shown ? "Hide" : "Show"} quoted history`}</span>
        </button>
      )}
      {original && (
        <button
          type="button"
          className="lcr-quote-toggle lcr-look-toggle"
          aria-controls={props.frameId}
          onClick={original.toggle}
        >
          {original.shown ? <MoonIcon size={16} /> : <SunIcon size={16} />}
          <span>{original.shown ? "Back to dark" : "Show original"}</span>
        </button>
      )}
    </div>
  );
}

/** One email: the frame, its quoted-history pill, what happened to its pictures, its image previews. */
export function MailFrame(props: {
  display: EmailDisplay;
  label: string;
  /** Null where the reader has no picture routes: pictures stay quiet stand-ins. */
  images: MailFrameImages | null;
  attachments?: readonly { id: string; name: string }[];
  attachmentHref?: (id: string) => string;
  /** Names the message a folded quote copies, from the ids that proved it; absent, a plain pill. */
  quotedSource?: (ids: readonly string[]) => QuotedSource | null;
}) {
  const { display } = props;
  const frame = useRef<HTMLIFrameElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const frameId = useId();
  const [quoted, setQuoted] = useState(false);
  const [original, setOriginal] = useState(false);
  const near = useNear(wrapper);
  const collapsed = display.regions.some((region) => region.kind === "duplicate_quote");
  const dark = useContext(DarkThemeContext);
  const pictures = usePictures({
    display,
    images: props.images,
    hasFiles: (props.attachments?.length ?? 0) > 0,
    quoted,
    near,
    dark,
    original,
  });
  const { height, measure } = useFrameHeight(frame, pictures.srcDoc, pictures.states);
  // A conversation just opened is shown once this email has loaded and has its height.
  const [loaded, setLoaded] = useState(false);
  useSettledAt(loaded);

  return (
    <div className="lcr-mail-frame" ref={wrapper} aria-busy={pictures.loading || undefined}>
      {display.fold.kind === "shown" && display.fold.reason === "inline_reply" && (
        <p className="lcr-mail-note">
          <span className="lcr-pill lcr-pill-amber">Reply inside quote</span>
          <span>This email answers inside the quoted text, so all of it is shown.</span>
        </p>
      )}
      <iframe
        ref={frame}
        id={frameId}
        title={props.label}
        sandbox={EMAIL_FRAME_SANDBOX}
        data-look={
          pictures.look === "email" ? "email" : pictures.look === "email-dark" ? "adapted" : "app"
        }
        srcDoc={pictures.srcDoc}
        referrerPolicy="no-referrer"
        style={{ height: height || undefined }}
        onLoad={() => {
          const body = frame.current?.contentDocument?.body;
          // Never fold a message down to nothing, whatever detection decided.
          if (!quoted && collapsed && body && !body.innerText.trim()) setQuoted(true);
          applyStates(frame.current, pictures.states);
          // Its height as soon as its document is in, not at the first timer after.
          measure();
          setLoaded(true);
        }}
      />
      {pictures.loading && (
        <p className="lcr-visually-hidden" role="status">
          Loading images
        </p>
      )}
      <MailActions
        frameId={frameId}
        quoted={collapsed ? { shown: quoted, toggle: () => setQuoted(!quoted) } : null}
        source={collapsed ? (props.quotedSource?.(quotedIds(display)) ?? null) : null}
        original={
          pictures.adaptable ? { shown: original, toggle: () => setOriginal(!original) } : null
        }
      />
      {/* Under the email, so the lead's words come before anything the app has to say about them. */}
      {display.fold.kind === "shown" && display.fold.reason === "unconfirmed" && (
        <p className="lcr-mail-note lcr-mail-note-quiet">
          Shown in full, because it quotes mail that is not in this thread.
        </p>
      )}
      {pictures.note && (
        <PictureNoteView note={pictures.note} onShow={pictures.showOnce} onRetry={pictures.retry} />
      )}
      {props.attachments && props.attachmentHref && (
        <Previews
          assets={pictures.previews}
          states={pictures.states}
          attachments={props.attachments}
          attachmentHref={props.attachmentHref}
        />
      )}
    </div>
  );
}
